<template>
  <div class="class-select">
    <div class="card">
      <div class="class-select__controls">
        <div class="year-term-selectors">
          <b-field label="Year" label-position="on-border">
            <b-select v-model="localSelectedYear" expanded>
              <option v-for="year in years" :key="year" :value="year">
                {{ year }}
              </option>
            </b-select>
          </b-field>

          <b-field label="Term" label-position="on-border">
            <b-radio-button
              v-for="term in terms"
              :key="term.label"
              v-model="localSelectedTerm"
              :native-value="term.value"
              type="is-info is-light is-outlined"
            >
              {{ term.label }}
            </b-radio-button>
          </b-field>
        </div>

        <div>
          <div
            class="class-list-container"
            :class="{ 'has-classes': classList.length > 0 }"
          >
            <div
              v-if="
                classList.length === 0 &&
                !isLoading &&
                localSelectedYear &&
                localSelectedTerm
              "
              class="no-classes-message"
            >
              <i class="fas fa-info-circle"></i>
              No classes found for the selected year and term.
            </div>

            <div v-else-if="isLoading" class="loading-message">
              <i class="fas fa-spinner fa-spin"></i>
              Loading classes...
            </div>

            <div
              v-else-if="classList.length > 0"
              class="class-list"
              :class="{ 'is-loading': isLoading }"
            >
              <div
                v-for="(item, index) in classList"
                :key="item.classId"
                @click="selectClass(index)"
              >
                <ClassDisplayItem
                  :class-data="item"
                  :is-selected="selectedClassIndex === index"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import sacService from "@/services/sac";
import { TERMS } from "@shared/constants";
import { recentYears } from "@shared/utils/date-helper";
import getTermForDate from "@shared/utils/term-helper";
import { mapActions, mapGetters } from "vuex";
import ClassDisplayItem from "./ClassDisplayItem.vue";

export default {
  name: "ClassSelect",

  components: {
    ClassDisplayItem,
  },

  props: {
    initialOpen: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["class-selected", "close-modal"],

  data() {
    return {
      isLoading: false,
      years: [],
      terms: TERMS,
      localSelectedYear: new Date().getFullYear(),
      localSelectedTerm: 0,
      classList: [],
    };
  },

  computed: {
    ...mapGetters({
      currentClass: "getCurrentClass",
    }),

    selectedKey() {
      return `${this.localSelectedYear}-${this.localSelectedTerm}`;
    },

    selectedClassIndex() {
      if (this.currentClass && this.classList.length > 0) {
        return this.classList.findIndex(
          (c) =>
            c.classId === this.currentClass.classId &&
            c.classDateId === this.currentClass.classDateId
        );
      }
      return -1;
    },
  },

  watch: {
    currentClass: {
      immediate: true,
      handler(newCurrentClass) {
        console.log("currentClass changed:", newCurrentClass);
        if (newCurrentClass && newCurrentClass.year && newCurrentClass.term) {
          this.$nextTick(() => {
            this.localSelectedYear = newCurrentClass.year;
            this.localSelectedTerm = newCurrentClass.term;
          });
        }
      },
    },

    selectedKey: {
      immediate: true,
      handler() {
        if (this.localSelectedYear && this.localSelectedTerm) {
          this.loadClassList(this.localSelectedYear, this.localSelectedTerm);
        }
      },
    },
  },

  methods: {
    ...mapActions({
      updateCurrentClass: "updateCurrentClass",
      updateClassList: "updateClassList",
      updateCurrentLecture: "updateCurrentLecture",
    }),

    async loadClassList(year, term) {
      if (!year || !term) {
        return;
      }

      this.isLoading = true;
      this.classList = [];

      try {
        const classList = await sacService.getClassList(year, term);

        // deduplicate class list
        const map = new Map();
        classList.forEach((item) => {
          const key = `${item["schoolName"]}-${item["groupName"]}`;
          if (!map.has(key)) {
            map.set(key, item);
          }
        });
        this.classList = Array.from(map.values());
        this.updateClassList(this.classList);
      } catch (err) {
        console.error(err);
        sacService.logout();
        this.$router.replace({ name: "login" });
      } finally {
        this.isLoading = false;
      }
    },

    async selectClass(selectedIndex) {
      if (!this.localSelectedYear || !this.localSelectedTerm) {
        return;
      }

      const classInfo = this.classList[selectedIndex];
      if (!classInfo) {
        return;
      }

      // Don't reselect the same class
      if (this.selectedClassIndex === selectedIndex) {
        return;
      }

      // CRITICAL: Clear current lecture when switching classes
      // This prevents stale lecture data from being merged with new class data
      this.updateCurrentLecture(null);

      this.updateCurrentClass({
        year: this.localSelectedYear,
        term: this.localSelectedTerm,
        ...classInfo,
      });

      this.$emit("class-selected", classInfo);

      this.$emit("close-modal");
    },
  },

  created() {
    this.years = recentYears(10);

    // Set defaults only if no current class (watcher will override if there is one)
    this.localSelectedYear = new Date().getFullYear();
    this.localSelectedTerm = getTermForDate(new Date());
  },
};
</script>

<style scoped>
.class-select__controls {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
}

.year-term-selectors {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  flex-wrap: wrap;
}

/* Class list container */
.class-list-container {
  min-height: auto;
  max-height: 60vh;
  overflow-y: auto;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
}

/* Status messages */
.no-classes-message,
.loading-message {
  padding: 1rem;
  text-align: center;
  color: #7a7a7a;
  font-style: italic;
}

.no-classes-message i,
.loading-message i {
  margin-right: 0.5rem;
}

.class-list.is-loading {
  opacity: 0.6;
  pointer-events: none;
}

.card {
  margin-bottom: 0;
  box-shadow: none;
  border-radius: 0 0 6px 6px;
}
</style>
